// Host for the sidebar docked BETWEEN the two split-screen panes — REQ-399, review #3 on #1264.
//
// Apryse has no panel location for "between the panes". `UI.addPanel` only knows left/right (and
// their RTL aliases start/end), so a third Apryse panel would dock OUTSIDE both panes — the one
// place the review asked it not to be. The sidebar is therefore our own node.
//
// It is a real FLEX CHILD of `.MultiViewer`, a SIBLING of the two panes. That sibling relationship
// is the whole point: `.MultiViewer` is a flex row and the panes are `flex: 0 1 auto` with equal
// bases, so the overflow our fixed-width panel introduces is shrunk out of BOTH of them equally
// (~165px each for a 330px panel). The first version parented the panel inside `#container1` and
// reserved its width with padding, which made the LEFT screen pay the entire cost while the right
// pane kept its full width — visibly lopsided, and the reason for this refactor.
//
// The node is APPENDED LAST and moved into place with CSS `order` (see the stylesheet). Appending
// is the safest way to add a child to a list React owns — no React-rendered sibling ever gets an
// unexpected node inserted before it — while `order` still paints it between the left pane and the
// divider.
//
// `.MultiViewer` is rendered by Apryse whether or not split screen is on (it just carries a
// `hidden` class), but the document area is re-mounted around it, so `sync` is idempotent and is
// re-run on every `layoutEpoch` from splitScreen.store.

// Apryse's own root for the side-by-side layout (MultiViewer.js); not configurable.
const MULTI_VIEWER_SELECTOR = ".MultiViewer"

// Marks our host node. The layout rules live in public/webviewer-quotation.css alongside the rest
// of the split-screen chrome, since they style Apryse's elements (pane order, min-width) as much as
// ours. Hiding is plain `display: none`, which takes the panel out of the flex line entirely and
// hands its width straight back to the two panes — no extra state for the stylesheet to read.
export const MIDDLE_PANEL_CLASS = "asq-middle-panel"

export type TMiddlePanelHost = {
	// Stable across attach/detach cycles, so the React root created over it survives them too.
	container: HTMLElement
	// (Re)parent the host into the split-screen root that exists NOW, or detach it when split
	// screen is closed. Returns whether the host is currently attached.
	sync: (options: { shadowRoot: ShadowRoot | null | undefined; isSplitActive: boolean; isOpen: boolean }) => boolean
	// Publish which pane currently owns the selection driving the sidebars (#1311, AC7), as
	// `data-primary-pane` on THIS node — the only node in the split layout we own. The pane
	// containers are Apryse React-owned and lose any attribute we set on their next render, so the
	// stylesheet reaches the pane FROM here, with `:has()`. Separate from `sync` on purpose: it is
	// called on every selection, and sync also asks Apryse to re-lay-out both viewers.
	setPrimaryPane: (pane: "primary" | "split") => void
	destroy: () => void
}

export const createMiddlePanelHost = (): TMiddlePanelHost => {
	const container = document.createElement("div")
	container.className = MIDDLE_PANEL_CLASS
	// The panes claim clicks anywhere inside them ("activate this viewer" / empty-space deselect),
	// and the sidebar is a separate surface — keep its clicks to itself.
	container.addEventListener("mousedown", (event) => event.stopPropagation())

	const detach = () => container.remove()

	const sync: TMiddlePanelHost["sync"] = ({ shadowRoot, isSplitActive, isOpen }) => {
		const root = isSplitActive ? (shadowRoot?.querySelector(MULTI_VIEWER_SELECTOR) as HTMLElement | null) : null
		if (!root?.isConnected) {
			detach()
			return false
		}
		// Always re-append rather than insert: `order` owns the visual placement, so the DOM
		// position only has to be somewhere React will not trip over.
		if (container.parentElement !== root) root.appendChild(container)
		container.style.display = isOpen ? "" : "none"
		return true
	}

	const setPrimaryPane: TMiddlePanelHost["setPrimaryPane"] = (pane) => {
		container.dataset.primaryPane = pane
	}
	setPrimaryPane("primary")

	return { container, sync, setPrimaryPane, destroy: detach }
}
